import DateTextField from '@brands/Components/Inputs/MuiDateTextField/DateTextField';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import { useWindowSize } from '@brands/hooks';
import { getCasePatientRx } from '@brands/services/cases/getCasePatientRx';
import { ICase } from '@brands/services/cases/types/ICase';
import { checkAndSetPrescriptionDate } from '@brands/Utils/checkAndSetDate';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal, { Styles } from 'react-modal';
import { useParams } from 'react-router-dom';

import { OralHealthReportSubmit } from '../../../assets/types';
import PharmacySection from '../../PharmacySection/PharmacySection';
import { prescriptionValidationSchema } from './prescriptionValidationSchema';
import styles from './styles.module.scss';

export type PrescriptionForm = {
  drug: string;
  sig: string;
  prescription_date: string;
  dispense: string;
  refills: string;
  pharmacy_name: string;
  pharmacy_street_address: string;
  pharmacy_city: string;
  pharmacy_state: string;
  pharmacy_zip_code: string;
  pharmacy_phone_number: string;
};

type PrescriptionModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: PrescriptionForm) => void;
  initialData?: PrescriptionForm | null;
  editingIndex: number | null;
  consultationForm: OralHealthReportSubmit;
  setConsultationForm: React.Dispatch<React.SetStateAction<OralHealthReportSubmit>>;
  thisCase: ICase;
  setCase: React.Dispatch<React.SetStateAction<ICase>>;
};

const emptyForm: PrescriptionForm = {
  drug: '',
  sig: '',
  prescription_date: '',
  dispense: '',
  refills: '',
  pharmacy_name: '',
  pharmacy_street_address: '',
  pharmacy_city: '',
  pharmacy_state: '',
  pharmacy_zip_code: '',
  pharmacy_phone_number: '',
};

const PrescriptionModal = ({
  open,
  onClose,
  onSave,
  initialData,
  editingIndex,
  consultationForm,
  setConsultationForm,
  thisCase,
  setCase,
}: PrescriptionModalProps) => {
  const screenSize = useWindowSize();
  const isMobile = useMemo(() => screenSize.width < 768, [screenSize.width]);
  const routeCaseId = useParams().caseId as string;
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const getDefaultValues = (): PrescriptionForm => ({
    ...emptyForm,
    prescription_date: dayjs().format('MM/DD/YYYY'),
    pharmacy_name: consultationForm?.pharmacy_name ?? '',
    pharmacy_street_address: consultationForm?.pharmacy_street_address ?? '',
    pharmacy_city: consultationForm?.pharmacy_city ?? '',
    pharmacy_state: consultationForm?.pharmacy_state ?? '',
    pharmacy_zip_code: consultationForm?.pharmacy_zip_code ?? '',
    pharmacy_phone_number: consultationForm?.pharmacy_phone_number ?? '',
  });

  const {
    register,
    setValue,
    watch,
    clearErrors,
    trigger,
    formState: { errors, isDirty },
    reset,
  } = useForm<PrescriptionForm>({
    resolver: yupResolver(prescriptionValidationSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: getDefaultValues(),
  });

  const mobileCustomStylesDashboard: Styles = {
    ...customStylesDashboard,
    overlay: {
      ...(customStylesDashboard?.overlay || {}),
      overflowY: 'scroll',
      overflowX: 'hidden',
    },
    content: {
      ...(customStylesDashboard?.content || {}),
      width: '100vw',
      height: '100vh',
      position: 'fixed',
      top: '50%',
      left: '50%',
      padding: '90px 16px 32px 16px',
      transform: 'translate(-50%, -50%)',
      right: '0',
      bottom: '0',
      margin: '0',
      borderRadius: '0',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      overflowY: 'scroll',
    },
  };

  const customStylesDashboardDesktop: Styles = {
    ...customStylesDashboard,
    overlay: {
      ...customStylesDashboard.overlay,
    },
    content: {
      ...customStylesDashboard.content,
      flexDirection: 'column',
      width: '444px',
      padding: '30px 32px 32px',
      overflowY: 'auto',
      maxHeight: '750px',
      justifyContent: 'flex-start',
    },
  };

  const handleSave = async () => {
    const isValid = await trigger();

    if (!isValid) {
      const firstError = Object.values(errors)[0];
      if (firstError) {
        displayErrorDetails(firstError);
      }
      return;
    }

    const data = watch();

    const payload = {
      ...data,
      prescription_date: data.prescription_date ? dayjs(data.prescription_date).format('MM/DD/YYYY') : '',
    };

    await onSave(payload);
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    if (initialData) {
      reset({
        ...emptyForm,
        ...initialData,
        prescription_date: initialData.prescription_date
          ? dayjs(initialData.prescription_date).format('MM/DD/YYYY')
          : '',
      });
    } else {
      reset(getDefaultValues());
    }
  }, [initialData, open, reset]);

  const addInitialOneReportObjects = async (): Promise<void> => {
    try {
      const caseId = parseInt(routeCaseId, 10);
      window.open('', 'formpopup', 'width=1500,height=1000,resizeable,scrollbars');
      const icorePayload = await getCasePatientRx(caseId);

      const loginform = document.getElementById('icoreForm');
      const loginInput = document.getElementById('icoreFormData');

      if (loginform && loginInput) {
        (loginform as HTMLFormElement).target = 'formpopup';
        (loginInput as HTMLInputElement).value = JSON.stringify(icorePayload);
        (loginform as HTMLFormElement).submit();
      }
    } catch (error) {
      displayErrorDetails(`Error adding new report objects: ${error}`);
    }
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      ariaHideApp={false}
      style={isMobile ? mobileCustomStylesDashboard : customStylesDashboardDesktop}
    >
      <div className={`${styles.body} ${styles.formContainer}`}>
        <div className={styles.prescriptionModal}>
          <div className={styles.bodyHead}>
            <CloseIcon onClick={onClose} />
          </div>

          <div className={`${styles.body} ${styles.formContainer}`}>
            <span className={styles.bodyHeader}>Add prescription</span>

            <Button variant="contained" className={styles.bodyBtn} onClick={addInitialOneReportObjects}>
              Access ePrescribe platform
            </Button>

            <div className={styles.bodySub}>
              <span>Prescription information</span>

              <TextField
                {...register('drug')}
                name="drug"
                errors={errors.drug ? errors : {}}
                label="Drug"
                required
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  if (errors.drug) clearErrors('drug');
                  setValue('drug', e.target.value, { shouldValidate: true, shouldDirty: true });
                }}
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />

              <TextField
                {...register('sig')}
                name="sig"
                errors={errors.sig ? errors : {}}
                label="Sig"
                required
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  if (errors.sig) clearErrors('sig');
                  setValue('sig', e.target.value, { shouldValidate: true, shouldDirty: true });
                }}
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />

              <DateTextField
                register={register}
                label="Prescription date"
                name="prescription_date"
                data-testid="prescription_date"
                required
                errors={errors}
                value={watch('prescription_date') ? dayjs(watch('prescription_date')) : null}
                onChange={(newValue: any) => {
                  if (errors.prescription_date) clearErrors('prescription_date');

                  const formattedDate =
                    newValue && dayjs(newValue).isValid() ? dayjs(newValue).format('MM/DD/YYYY') : '';

                  setValue('prescription_date', formattedDate, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileDOB}`}
              />

              <div className={styles.rowDisplay}>
                <TextField
                  {...register('dispense')}
                  name="dispense"
                  errors={errors.dispense ? errors : {}}
                  label="Dispense"
                  required
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    if (errors.dispense) clearErrors('dispense');
                    setValue('dispense', e.target.value, { shouldValidate: true, shouldDirty: true });
                  }}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                />

                <TextField
                  {...register('refills')}
                  name="refills"
                  errors={errors.refills ? errors : {}}
                  label="Refills"
                  required
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    if (errors.refills) clearErrors('refills');
                    setValue('refills', e.target.value, { shouldValidate: true, shouldDirty: true });
                  }}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                />
              </div>

              <span />

              <div className={styles.subjective}>
                <span className={`fs-unmask ${styles.subjectiveSpan}`}>Pharmacy information</span>
              </div>

              <PharmacySection
                consultationForm={consultationForm}
                setConsultationForm={setConsultationForm}
                thisCase={thisCase}
                setCase={setCase}
                register={register}
                errors={errors}
                setValue={setValue}
                watch={watch}
              />
            </div>
          </div>

          <div className={styles.bodyFooter}>
            <Button variant="contained" className={styles.containedBtn} onClick={handleSave} disabled={!isDirty}>
              Add prescription
            </Button>
            <Button variant="text" className={styles.textBtn} onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PrescriptionModal;
